import Button from "atoms/buttons/button";
import Heading from "atoms/typography/heading";
import Icon from "atoms/icons/icon";
import Modal from "molecules/modals/modal";
import React, { useEffect, useMemo, useState } from "react";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import { ModalTypes } from "molecules/constants/modal-types";
import { useLocalization } from "utilities/hooks/use-localization";
import useNotebooksAtom from "utilities/hooks/domain/notebooks/use-notebooks-atom";
import PageRecord from "models/view-models/page-record";
import StringUtils from "utilities/string-utils";
import useRelocatePage from "utilities/hooks/domain/notebooks/pages/use-relocate-page";
import Paragraph from "atoms/typography/paragraph";
import { RouteUtils } from "utilities/route-utils";
import { siteMap } from "internal-sitemap";
import { useHistory } from "react-router";
import useCurrentPage from "utilities/hooks/domain/notebooks/pages/use-current-page";
import { ToastManager } from "utilities/toast/toast-manager";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import useBreakpoint, {
    BreakpointComparer,
} from "utilities/hooks/use-breakpoint";
import { ModalCloseButtonStyle } from "molecules/constants/modal-close-button-style";
import SelectExistingOrNewNotebookInput from "organisms/select-existing-or-new-notebook-input/select-existing-or-new-notebook-input";
import NotebookRecord from "models/view-models/notebook-record";
import useCurrentIdentity from "utilities/hooks/use-current-identity";
import useModalActions from "utilities/hooks/use-modal-actions";
import useNotebookPermissionsApi from "utilities/hooks/domain/notebooks/use-notebook-permissions-api";
import { ConfirmationModal } from "molecules/modals/confirmation-modal";

interface RelocatePageModalProps {
    onSuccess?: (clonedPage: any) => void;
    onError?: (errors: string[]) => void;
    page: PageRecord;
    isVisible: boolean;
    onClose: () => void;
}

const CSS_CLASS_NAME: string = "c-copy-page-modal";

export const RelocatePageModal: React.FC<RelocatePageModalProps> = ({
    onSuccess,
    onError,
    page,
    isVisible = false,
    onClose,
}: RelocatePageModalProps): JSX.Element => {
    const { t } = useLocalization();
    const history = useHistory();
    const { currentPage } = useCurrentPage();
    const { currentIdentity } = useCurrentIdentity();

    const {
        resultObject: notebooks,
        refresh: getAllNotebooks,
        createNotebook,
    } = useNotebooksAtom();
    const [selectedNotebookId, setSelectedNotebookId] = useState<
        number | string
    >(0);

    const isMobile = useBreakpoint(
        Breakpoints.SmallTablet,
        BreakpointComparer.MaxWidth
    );
    const modalType = isMobile ? ModalTypes.Bottom : ModalTypes.Base;

    const redirectToRelocatedPage = (clonedPage: PageRecord) => {
        const route = RouteUtils.getUrl(siteMap.notebooks.notebook.page, {
            notebookId: clonedPage.notebookId,
            pageId: clonedPage.id,
        });

        if (route) {
            history.push(route);
        }
    };

    const { relocatePage, loading } = useRelocatePage({
        onSuccess: (page) => {
            onClose?.();
            onSuccess?.(page);
            if (page.id === currentPage?.id) {
                redirectToRelocatedPage(page);
            }

            const destinationNotebook = notebooks.find(
                (n) => n.id === page.notebookId
            );

            ToastManager.success(
                t("page-success-move", {
                    pageName: page.name,
                    notebookName: destinationNotebook?.name ?? t("selected"),
                })
            );
        },
        onError: (errorMessages) => {
            onError?.(errorMessages);
        },
    });

    const availableNotebooks = notebooks.filter(
        (notebook) =>
            notebook.id !== 0 &&
            notebook.id !== page.notebookId &&
            notebook.ownerId === currentIdentity?.userId()
    );

    useEffect(() => {
        if (isVisible && page) {
            setSelectedNotebookId(availableNotebooks[0]?.id ?? 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, isVisible]);

    useEffect(() => {
        if (isVisible && notebooks.length === 0) {
            getAllNotebooks();
        }
    }, [notebooks.length, getAllNotebooks, isVisible]);

    const handleNotebookChange = (selectedNotebook: number | string) => {
        setSelectedNotebookId(selectedNotebook);
    };

    const createNewNotebook = async (
        newNotebookName: string
    ): Promise<number | null> => {
        if (!isValidNotebookSelected) {
            return null;
        }

        try {
            const notebook = await createNotebook(
                new NotebookRecord({
                    ownerId: currentIdentity?.userId(),
                    name: newNotebookName,
                })
            );

            if (notebook?.id) {
                setSelectedNotebookId(notebook.id);
                return notebook.id;
            } else {
                throw new Error("Notebook creation failed");
            }
        } catch (error) {
            ToastManager.error(
                t("errors-actionResource", {
                    action: t("creating"),
                    resource: t("notebook"),
                })
            );
            return null;
        }
    };
    const handleFormReset = () => {
        onClose?.();
    };

    const {
        isOpen: isSharedAlertOpen,
        handleOpen: handleSharedAlertOpen,
        handleClose: handleSharedAlertClose,
    } = useModalActions();

    const currentNotebook = notebooks.filter(
        (notebook) => notebook.id === page.notebookId
    )[0];

    const { notebookPermissions } = useNotebookPermissionsApi({
        notebook: currentNotebook && currentNotebook,
    });
    const handleRelocateRequest = () => {
        if (
            notebookPermissions &&
            notebookPermissions?.resultObjects.length > 0
        ) {
            handleSharedAlertOpen();
            return;
        }
        handleRelocateClick();
    };
    const handleRelocateClick = async () => {
        let notebookId: number;

        if (typeof selectedNotebookId === "string") {
            const createdNotebookId =
                await createNewNotebook(selectedNotebookId);

            if (createdNotebookId == null) {
                return;
            }

            notebookId = createdNotebookId;
        } else {
            notebookId = selectedNotebookId;
        }
        await relocatePage(page, notebookId);
        getAllNotebooks();
    };

    const isValidNotebookSelected = useMemo(() => {
        if (typeof selectedNotebookId === "string") {
            return StringUtils.hasValue(selectedNotebookId.trim());
        } else {
            return selectedNotebookId > 0;
        }
    }, [selectedNotebookId]);
    const canRelocate = isValidNotebookSelected && !loading;

    return (
        <>
            <Modal
                closeButtonStyle={ModalCloseButtonStyle.Hidden}
                closeDialog={onClose}
                cssClassName={CSS_CLASS_NAME}
                isVisible={isVisible}
                label={t("move")}
                stopEventPropagation={true}
                type={modalType}>
                <Heading cssClassName={`${CSS_CLASS_NAME}__heading`}>
                    {t("move")} {StringUtils.capitalize(t("page"))}
                    <Button
                        accessibleText={t("close")}
                        cssClassName={"-modal-close -icon"}
                        onClick={onClose}
                        size={ButtonSizes.Medium}
                        style={ButtonStyles.None}>
                        <Icon type={Icons.Close} size={IconSizes.Large} />
                    </Button>
                </Heading>
                <div className={`${CSS_CLASS_NAME}__content`}>
                    <Paragraph>{page.name}</Paragraph>
                    <SelectExistingOrNewNotebookInput
                        availableNotebooks={availableNotebooks}
                        onSelectedNotebookChange={handleNotebookChange}
                        selectedNotebook={selectedNotebookId}
                    />
                </div>
                <div className={`${CSS_CLASS_NAME}__actions`}>
                    <Button
                        accessibleText={t("cancel")}
                        onClick={handleFormReset}
                        style={ButtonStyles.Secondary}>
                        {t("cancel")}
                    </Button>
                    <Button
                        accessibleText={t("copy")}
                        disabled={!canRelocate}
                        onClick={handleRelocateRequest}
                        style={ButtonStyles.Primary}>
                        {t("move")}
                    </Button>
                </div>
            </Modal>
            <ConfirmationModal
                isVisible={isSharedAlertOpen}
                message={t("movingSharedNotebookWarning")}
                onCancel={handleSharedAlertClose}
                onConfirm={handleRelocateClick}
            />
        </>
    );
};
